import { IconSize } from '@sidecar-components/atoms/Icon'
import { classify } from 'sidecar-utilities'

// NOTE:
// if you make changes to these styles, see if you need to make similar
// changes to apps/member/src/components/_common/Payment/StripeForm/styles.js
// Those styles mirror these.

// This fancy offset is used to center align things that are
// absolutely positioned. top-1/2 will align the top of the item
// to the middle (too low), then the translate will push it back up
// exactly half of the items height (vertically centered). Used for
// icons, buttons, labels, placeholders. Input text is controlled by
// the padding of the input element itself.
const middleAlign = 'top-1/2 -translate-y-1/2'

// While the icon for the button is wrapped in an actual button, the button
// is only for functionality. Icons are the things that get styled.
// Size is a separate prop, not part of the className (see Icon for info)
// In the case of IconButton, the absolute is made !important so that it
// overrides the relative positioning of the IconButton.
export const ornamentSize = condensed => (condensed ? IconSize.SM : IconSize.MD)
export const ornamentStyles = `!absolute ${middleAlign}`

// The "field" is basically the div that wraps the everything. It helps
// keep everything together inside of the input. The input is made to
// visually encompass these things with extra padding so that we can leverage
// it's hover, focus and other states for styling.
export const fieldStyles = 'relative flex w-full'

export const inputStyles = ({
  button,
  condensed,
  disabled,
  error,
  icon,
  label,
  prefix = undefined,
  suffix = undefined,
  isLink = false,
}) =>
  classify({
    layout: 'w-full rounded-lg border peer',
    color: 'color-bgDefault color-textOnDefault',
    spacing: condensed ? 'py-3' : 'pt-7 pb-3',
    height: condensed ? 'h-12' : 'h-16',
    cursor: !disabled
      ? 'cursor-text'
      : isLink
        ? 'cursor-pointer'
        : 'cursor-not-allowed',
    // If the field is disabled, we don't do any of the
    // interactive styling (hover, focus, etc).
    disabledBorder: disabled && 'color-border-disabled',
    errorBorder:
      error &&
      'color-borderRisk hover:color-borderRisk-hover active:color-borderRisk-active',
    defaultBorder:
      !disabled &&
      !error &&
      'color-borderDefaultDecorStrong hover:color-borderDefaultDecorStrong-hover',
    active:
      !disabled &&
      !error &&
      'active:color-borderDefaultDecorStrong-active active:outline active:outline-4 active:color-outlinePrimaryDecor',
    focused:
      !disabled &&
      'focus:outline focus:outline-4 focus:color-borderDefaultDecorStrong-active focus:color-outlinePrimaryDecor',
    withPlaceholder:
      label &&
      `placeholder:text-transparent focus:placeholder:color-text-disabled`,
    // Used for labels, placeholders and input text these spacings are for when
    // we have icons and buttons or not. If these things are present, these give
    // more space so that text doesn't overlap them. `padding*` is for the input,
    // and `position*` for absolutely positioned items (label, icon, button).
    // The left-most value is the default, the middle is padding for the
    // condensed size and the right-most value is the max spacing needed.
    paddingWithIcon: icon
      ? condensed
        ? 'pl-10'
        : 'pl-13'
      : prefix
        ? 'pl-8'
        : 'pl-4',
    paddingWithBtn: button
      ? condensed
        ? 'pr-10'
        : 'pr-13'
      : suffix
        ? 'pr-8'
        : 'pr-4',
  })

// The label comes after the input so that the input can be used as a "peer"
// and control the way that the label looks depending on it's various states.
// The label should be positioned right where the input text will be and it
// should move out of the way when a user focuses to type. It should also
// help display the state of the field.
export const labelStyles = ({ condensed, error, icon, value }) =>
  classify({
    layout: `absolute ${middleAlign}`,
    color: error ? 'color-textRisk' : 'color-textPrimary',
    disabled: 'peer-disabled:color-text-disabled',
    invalid: 'peer-invalid:color-textRisk',
    focused: condensed
      ? 'peer-focus:hidden'
      : 'peer-focus:text-xs peer-focus:top-3 peer-focus:color-textPrimary peer-focus:translate-y-0',
    animation: 'transition-all',
    withValue: value && (condensed ? 'hidden' : 'text-xs top-3 translate-y-0'),
    positionWithIcon: !icon ? 'left-4' : condensed ? 'left-10' : 'left-13',
    makeNotSelectable: 'pointer-events-none',
  })

// Affixes are fixed text like a '$' prefix or a '%' suffix that sit on the
// input's text line, not centered like an icon, so they read as part of the
// value. They come after the input so they can use peer-* classes: they stay
// hidden while the label rests where the text goes, and show once the label
// moves out of the way.
export const affixStyles = ({ condensed, side = 'left', value }) =>
  classify({
    layout: 'absolute inset-y-0 items-center',
    position: side === 'right' ? 'right-4' : 'left-4',
    spacing: condensed ? 'py-3' : 'pt-7 pb-3',
    color: 'color-textOnDefault peer-disabled:color-text-disabled',
    visibility: value ? 'flex' : 'hidden peer-focus:flex',
    makeNotSelectable: 'pointer-events-none select-none',
  })
